import { AlertTriangle, CheckCircle2, HelpCircle, PlugZap, XCircle } from "lucide-react";

import type { ConfidenceLevel, FieldSource } from "@/api/types";
import { StatusBadge, type BadgeTone } from "@/components/StatusBadge";

/**
 * How sure the system is, in words (Frontend Spec §24).
 *
 * Five states, never a percentage sprinkled across the screen. The numeric score
 * exists on the record for a details view; an operator needs to know whether to
 * look twice, not to read three decimals.
 */
const LEVELS: Record<
  ConfidenceLevel,
  { label: string; tone: BadgeTone; icon: typeof CheckCircle2; hint: string }
> = {
  CERTAIN: {
    label: "Certain",
    tone: "available",
    icon: CheckCircle2,
    hint: "Identifié par un code-barres décodé — c'est un fait, pas une supposition.",
  },
  LIKELY: {
    label: "À vérifier",
    tone: "info",
    icon: HelpCircle,
    hint: "Le modèle propose une identification. Vérifiez avant de confirmer.",
  },
  UNCERTAIN: {
    label: "Ambigu",
    tone: "warning",
    icon: AlertTriangle,
    hint: "Plusieurs possibilités, ou une lecture peu sûre. Choisissez vous-même.",
  },
  DEGRADED: {
    label: "Analyse partielle",
    tone: "info",
    icon: PlugZap,
    // Deliberately not the same words as "Ambigu". Incomplete and unsure lead
    // to different actions, and telling an operator to look harder at an item
    // when the real problem is a sleeping Mac mini wastes their time.
    hint: "Une source n'a pas répondu. Ce qui a été lu reste utilisable.",
  },
  FAILED: {
    label: "Échec analyse",
    tone: "out",
    icon: XCircle,
    hint: "Rien n'a pu être lu. Saisissez l'article à la main — c'est toujours possible.",
  },
};

export function ConfidenceIndicator({
  level,
  showHint = false,
}: {
  level: ConfidenceLevel;
  showHint?: boolean;
}) {
  const entry = LEVELS[level];
  const Icon = entry.icon;
  return (
    <div className="flex flex-col gap-1">
      <StatusBadge tone={entry.tone}>
        <Icon className="size-3" aria-hidden />
        {entry.label}
      </StatusBadge>
      {showHint && <p className="text-[12px] text-ink-subtle">{entry.hint}</p>}
    </div>
  );
}

const SOURCE_LABELS: Record<FieldSource, string> = {
  BARCODE: "code-barres",
  OCR: "texte lu",
  VLM_LOCAL: "modèle local",
  VLM_CLOUD: "modèle cloud",
  CATALOGUE_MATCH: "catalogue",
  PRODUCT_DATA: "source externe",
  HUMAN: "saisi à la main",
  DEFAULT: "valeur par défaut",
};

/** Where a value came from. Shown on every proposed field, never hidden. */
export function SourceTag({ source, provider }: { source: FieldSource; provider?: string }) {
  const label = SOURCE_LABELS[source] ?? source;
  return (
    <span
      className="text-[10px] text-ink-subtle"
      title={provider ? `${label} · ${provider}` : label}
    >
      {label}
    </span>
  );
}
